import { Ban } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingField } from "@/components/settings/setting-field";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type CapacityFormData, isInfinityLimit } from "../types";
import { DailyLimitField } from "./daily-limit-field";
import { InfinityToggleInput } from "./infinity-toggle-input";

interface SimpleCapacityFormProps {
  saving?: boolean;
}

export function SimpleCapacityForm({ saving = false }: SimpleCapacityFormProps) {
  const { t } = useTranslation("settings");
  const { control } = useFormContext<CapacityFormData>();
  const [concurrentMax, dailyMax, durationAware] = useWatch({
    control,
    name: ["concurrentMax", "dailyMax", "durationAware"],
  });

  const formatLimit = (value: number) =>
    isInfinityLimit(value)
      ? t("capacity.basicIntro.noLimit")
      : t("capacity.basicIntro.limit", { count: value });

  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-lg lg:text-xl">
          <Ban className="w-6 h-6 mr-3 text-indigo-600 dark:text-indigo-400" />
          {t("capacity.capacityLimits")}
        </CardTitle>
        <p className="mt-2 text-sm text-muted-foreground">{t("capacity.basicIntro.description")}</p>
        <p className="mt-2 rounded-md bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-100">
          {t(
            durationAware
              ? "capacity.basicIntro.example"
              : "capacity.basicIntro.examplePerStartHour",
            {
              concurrent: formatLimit(concurrentMax),
              daily: formatLimit(dailyMax),
            }
          )}
        </p>
      </CardHeader>

      <CardContent className="px-6">
        <div className="space-y-6">
          {/* Concurrent Limit */}
          <div id="field-limits-concurrent-max" className="flex items-center justify-between">
            <SettingField
              label={
                durationAware
                  ? t("capacity.maxConcurrentAppointments")
                  : t("capacity.maxAppointmentsPerStartHour")
              }
              helpText={
                durationAware
                  ? t("capacity.concurrentAppointmentsDescription")
                  : t("capacity.appointmentsPerStartHourDescription")
              }
              className="text-base font-normal"
            />

            <InfinityToggleInput
              valueFieldName="concurrentMax"
              placeholder="10"
              min={1}
              max={100}
              saving={saving}
            />
          </div>

          <DailyLimitField saving={saving} />
        </div>
      </CardContent>
    </Card>
  );
}
